import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { RefreshCw, Search } from "lucide-react";
import Sparkline from "./Sparkline";
import KeywordTotals from "./KeywordTotals";
import GscPanel from "./GscPanel";
import { CORE_TARGETS } from "@/lib/targetKeyword";
import { fmtParam } from "@/lib/adminUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const norm = (s) => (s || "").toLowerCase().trim();
const COLS = ["Target keyword", "Type", "Landings", "From search", "Views", "Leads", "Conv.", "Top source", "14-day trend", "Google pos."];

function mergeRows(apiRows, showZero) {
  const byKw = new Map(apiRows.map((r) => [norm(r.keyword), r]));
  const zero = showZero
    ? CORE_TARGETS.filter((t) => !byKw.has(norm(t.keyword))).map((t) => ({ keyword: t.keyword, page_type: t.page_type, top_path: t.path, views: 0, sessions: 0, landings: 0, organic_landings: 0, leads: 0, conversion: 0, top_sources: [], trend: Array(14).fill(0) }))
    : [];
  return [...apiRows, ...zero];
}

function gscMatch(keyword, rows) {
  const k = norm(keyword);
  return rows.find((r) => norm(r.key) === k) || rows.find((r) => norm(r.key).includes(k) || (norm(r.key).length > 8 && k.includes(norm(r.key))));
}

function KeywordRow({ r, gscRows }) {
  const g = gscRows.length ? gscMatch(r.keyword, gscRows) : null;
  return (
    <tr className={`border-t border-slate-100 ${r.landings === 0 && r.views === 0 ? "opacity-60" : ""}`} data-testid="kw-row">
      <td className="p-3 text-sm font-semibold text-slate-900 max-w-xs">
        {r.keyword}
        {r.top_path && <div className="mono text-[10px] text-slate-400 font-normal truncate">{r.top_path}</div>}
      </td>
      <td className="p-3 mono text-[10px] tracking-[0.12em] uppercase text-slate-500">{r.page_type || "—"}</td>
      <td className="p-3 mono text-sm text-topo font-semibold">{r.landings}</td>
      <td className="p-3 mono text-sm text-emerald-700">{r.organic_landings}</td>
      <td className="p-3 mono text-xs text-slate-600">{r.views}</td>
      <td className="p-3 mono text-sm text-orange-survey font-semibold">{r.leads}</td>
      <td className="p-3 mono text-xs text-slate-600">{r.landings ? `${(r.conversion * 100).toFixed(1)}%` : "—"}</td>
      <td className="p-3 mono text-[10px] tracking-[0.1em] uppercase text-slate-600">{r.top_sources[0] ? `${r.top_sources[0].source} · ${r.top_sources[0].landings}` : "—"}</td>
      <td className="p-3"><Sparkline points={r.trend} width={84} height={20} /></td>
      <td className="p-3 mono text-xs" title={g ? `${g.clicks} clicks · ${g.impressions} impressions` : ""}>{g ? `#${g.position.toFixed(1)} · ${g.clicks}c` : "—"}</td>
    </tr>
  );
}

/** Admin keyword tracker: first-party landings per target keyword + optional Search Console data. */
export default function KeywordTracker({ token, range }) {
  const [data, setData] = useState(null);
  const [gsc, setGsc] = useState({ status: null, report: null, error: "" });
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [showZero, setShowZero] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const headers = { "X-Admin-Token": token };
    const params = {};
    if (range?.from) params.from = fmtParam(range.from);
    if (range?.to) params.to = fmtParam(range.to);
    try {
      const [kw, st] = await Promise.all([
        axios.get(`${API}/admin/keywords`, { headers, params }),
        axios.get(`${API}/admin/gsc/status`, { headers }),
      ]);
      setData(kw.data);
      let report = null;
      let error = "";
      if (st.data.connected) {
        try {
          report = (await axios.get(`${API}/admin/gsc/report`, { headers, params: { ...params, dimension: "query" } })).data;
        } catch (e) {
          error = e.response?.data?.detail || "Search Console request failed.";
        }
      }
      setGsc({ status: st.data, report, error });
    } finally {
      setLoading(false);
    }
  }, [token, range]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const merged = mergeRows(data?.keywords || [], showZero);
    return query ? merged.filter((r) => norm(r.keyword).includes(norm(query))) : merged;
  }, [data, showZero, query]);
  const targets = useMemo(() => new Set(CORE_TARGETS.map((t) => norm(t.keyword)).concat((data?.keywords || []).map((k) => norm(k.keyword)))), [data]);

  return (
    <section data-testid="keyword-tracker">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">Keyword rank tracker</div>
          <h2 className="display mt-2 text-2xl sm:text-3xl font-extrabold tracking-tighter text-slate-900">Which target keywords bring visitors</h2>
          <p className="mt-1 text-sm text-slate-500">Each landing page and guide is tagged with the keyword it targets. Visits are first-party; Google positions appear once Search Console is connected.</p>
        </div>
        <button onClick={load} data-testid="kw-refresh" className="inline-flex items-center gap-2 rounded-sm border border-slate-300 hover:border-slate-900 text-slate-900 text-sm font-semibold px-4 py-2 bg-white">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {data && <KeywordTotals totals={data.totals} channels={data.channels} />}

      <div className="flex flex-wrap items-center gap-3 mb-3">
        <label className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter keywords…" data-testid="kw-search" className="w-full border border-slate-300 focus:border-topo outline-none pl-9 pr-3 py-2 text-sm rounded-sm bg-white" />
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
          <input type="checkbox" checked={showZero} onChange={(e) => setShowZero(e.target.checked)} data-testid="kw-show-zero" className="accent-[#0F4C81]" />
          Show targets with no visits yet
        </label>
        <span className="ml-auto mono text-[10px] tracking-[0.18em] uppercase text-slate-400" data-testid="kw-row-count">{rows.length} keywords</span>
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto mb-8">
        <table className="w-full text-left" data-testid="kw-table">
          <thead className="bg-paper-dark/40">
            <tr>{COLS.map((c) => <th key={c} className="p-3 mono text-[10px] tracking-[0.18em] uppercase text-slate-500 whitespace-nowrap">{c}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r) => <KeywordRow key={r.keyword} r={r} gscRows={gsc.report?.rows || []} />)}
            {rows.length === 0 && <tr><td colSpan={COLS.length} className="p-4 text-sm text-slate-500">No keywords match.</td></tr>}
          </tbody>
        </table>
      </div>

      <GscPanel status={gsc.status} report={gsc.report} error={gsc.error} targets={targets} />
    </section>
  );
}
